import type { Story } from "@repo/fika-shared/src/loader/story-schema.js"
import { createEffect, createMemo, createSignal, Show } from "solid-js"
import { useAppContext } from "@/context/app"
import { fetchAndSave, files, removeFile } from "@/data/storage"
import { Icon } from "@/designSystem/components/icon"
import { buttonClass, progressClass, syncClass, textClass } from "./sync.css"

const [downloading, setDownloading] = createSignal(false)

export function Sync() {
  const { db, session } = useAppContext()
  const ids = createMemo(
    () => new Set(db.story.all().map((story: Story) => story.id)),
  )
  const syncedIds = createMemo(() => ids().intersection(files()))
  const toAdd = createMemo(() => ids().difference(files()))
  const toRemove = createMemo(() => files().difference(ids()))
  const done = createMemo(() => {
    const totalIds = ids().size
    if (totalIds === 0) return 1

    return Math.max(syncedIds().size / totalIds, 0.07)
  })

  createEffect(() => {
    if (!downloading()) return
    const id = toRemove().values().next().value
    if (!id) return

    removeFile(id)
  })

  createEffect(async () => {
    if (!downloading()) return
    const id = toAdd().values().next().value
    if (!id) return
    if (files().has(id)) return

    const story = db.story.get(id)
    if (!story) return

    try {
      // ensure we don't block the main thread, but still
      // have a decent throughtput (25 stories per second)
      await new Promise(resolve => setTimeout(resolve, 40))
      await fetchAndSave(id, story.url, session.jwt)
    } catch (err) {
      console.error(err)
    }
  })

  return (
    <Show when={toAdd().size > 0}>
      <div class={syncClass}>
        <div
          class={progressClass}
          style={{ width: `${Number(done() * 100)}%` }}
        />
        <button
          class={buttonClass}
          type="button"
          onClick={() => setDownloading(!downloading())}
        >
          <Icon name="download-cloud" size="18" />
          {downloading() ? "stop" : "download"}
        </button>
        <span class={textClass}>
          {syncedIds().size} of {ids().size} stories
        </span>
      </div>
    </Show>
  )
}
